import { Box, Button, Step, StepLabel, Stepper } from "@mui/material";
import { type SxProps, type Theme } from "@mui/material/styles";
import type { ReactNode } from "react";
import { useState } from "react";

const styles = {
  footer: {
    display: "flex",
    justifyContent: "space-between",
    marginTop: 3,
  },
  content: {
    marginTop: 3,
  },
} satisfies Record<string, SxProps<Theme>>;

/**
 * A single step within a {@link Wizard}.
 *
 * @property id A stable unique identifier for the step, used as the React key.
 * @property label The label rendered in the stepper header for this step.
 * @property render Returns the content shown while this step is active.
 * @property validate Runs when the user attempts to advance from this step.
 *   The wizard only advances to the next step when this resolves to `true`.
 */
export type WizardStep = {
  id: string;
  label: string;
  render: () => ReactNode;
  validate: () => Promise<boolean> | boolean;
};

type WizardProps = {
  readonly steps: readonly WizardStep[];
};

/**
 * A generic controlled multi-step wizard.
 *
 * Renders an MUI `Stepper` header plus the active step's content and a
 * navigation footer. Advancing is gated by the active step's `validate`
 * callback: the wizard only moves forward when validation resolves to `true`.
 * The final step renders no Next button, since its own content is expected to
 * provide a submit action.
 *
 * @param steps The ordered steps to render. Must be non-empty for the wizard
 *   to display meaningful content.
 */
export const Wizard = ({ steps }: WizardProps) => {
  const [index, setIndex] = useState(0);

  const isLastStep = index === steps.length - 1;
  const activeStep = steps[index];

  const handleNext = async () => {
    if (activeStep === undefined) {
      return;
    }
    const isValid = await activeStep.validate();
    if (isValid) {
      setIndex((current) => Math.min(current + 1, steps.length - 1));
    }
  };

  const handleBack = () => {
    setIndex((current) => Math.max(current - 1, 0));
  };

  return (
    <Box>
      <Stepper alternativeLabel activeStep={index}>
        {steps.map((step) => (
          <Step key={step.id}>
            <StepLabel>{step.label}</StepLabel>
          </Step>
        ))}
      </Stepper>
      <Box sx={styles.content}>{activeStep?.render()}</Box>
      <Box sx={styles.footer}>
        <Button disabled={index === 0} variant="outlined" onClick={handleBack}>
          Back
        </Button>
        {!isLastStep && (
          <Button
            data-testid="wizard-next"
            variant="contained"
            onClick={handleNext}
          >
            Next
          </Button>
        )}
      </Box>
    </Box>
  );
};
